/**
 * Routing core — graph, Dijkstra, and turn-by-turn narration.
 *
 * Deliberately knows nothing about where the graph came from. The sample estate
 * builds one from its corridor centrelines; an imported floor plan builds one
 * from the walkable pixels traced out of the drawing. Both hand the same shape
 * to `createRouter`, so wayfinding behaves identically whichever estate is on
 * screen — which is the whole reason directions stopped working on imported
 * plans in the first place.
 */

/** Metres per second. Mall pace with browsing, not a brisk walk. */
export const WALK_SPEED = 1.2;
/** Seconds of dwell per floor change (queue + ride). */
export const FLOOR_CHANGE_SECONDS = 45;
/** Equivalent metre cost of a floor change, so Dijkstra weighs it sensibly. */
export const FLOOR_CHANGE_COST = FLOOR_CHANGE_SECONDS * WALK_SPEED;

export const nodeKey = (floor, x, z) => `${floor}|${x.toFixed(1)},${z.toFixed(1)}`;

/** A mutable graph builder. Nodes are deduplicated by rounded position. */
export function makeGraph() {
    const nodes = new Map();
    const edges = new Map();

    const addNode = (floor, x, z, kind, label) => {
        const k = nodeKey(floor, x, z);
        if (!nodes.has(k)) nodes.set(k, { key: k, floor, x: +x.toFixed(1), z: +z.toFixed(1), kind, label });
        else if (label && !nodes.get(k).label) nodes.get(k).label = label;
        return nodes.get(k);
    };

    const addEdge = (a, b, kind, costOverride) => {
        if (!a || !b || a.key === b.key) return;
        const cost = costOverride != null
            ? costOverride
            : (kind === 'vertical' ? FLOOR_CHANGE_COST : Math.hypot(a.x - b.x, a.z - b.z));
        if (!edges.has(a.key)) edges.set(a.key, []);
        if (!edges.has(b.key)) edges.set(b.key, []);
        edges.get(a.key).push({ to: b.key, cost, kind });
        edges.get(b.key).push({ to: a.key, cost, kind });
    };

    return { nodes, edges, addNode, addEdge };
}

/** Dijkstra with a simple binary heap. */
export function shortestPath(graph, fromKey, toKey) {
    const { nodes, edges } = graph;
    if (!nodes.has(fromKey) || !nodes.has(toKey)) return null;

    const dist = new Map([[fromKey, 0]]);
    const prev = new Map();
    const visited = new Set();
    const heap = [{ k: fromKey, d: 0 }];

    const push = (item) => {
        heap.push(item);
        let i = heap.length - 1;
        while (i > 0) {
            const p = (i - 1) >> 1;
            if (heap[p].d <= heap[i].d) break;
            [heap[p], heap[i]] = [heap[i], heap[p]];
            i = p;
        }
    };
    const pop = () => {
        const top = heap[0];
        const last = heap.pop();
        if (heap.length) {
            heap[0] = last;
            let i = 0;
            for (;;) {
                const l = (2 * i) + 1;
                const r = l + 1;
                let s = i;
                if (l < heap.length && heap[l].d < heap[s].d) s = l;
                if (r < heap.length && heap[r].d < heap[s].d) s = r;
                if (s === i) break;
                [heap[s], heap[i]] = [heap[i], heap[s]];
                i = s;
            }
        }
        return top;
    };

    while (heap.length) {
        const { k, d } = pop();
        if (visited.has(k)) continue;
        visited.add(k);
        if (k === toKey) break;
        const list = edges.get(k) || [];
        for (const e of list) {
            if (visited.has(e.to)) continue;
            const nd = d + e.cost;
            if (nd < (dist.has(e.to) ? dist.get(e.to) : Infinity)) {
                dist.set(e.to, nd);
                prev.set(e.to, { from: k, kind: e.kind });
                push({ k: e.to, d: nd });
            }
        }
    }

    if (!dist.has(toKey)) return null;

    const path = [];
    let cur = toKey;
    while (cur) {
        const step = prev.get(cur);
        path.unshift({ node: nodes.get(cur), viaKind: step ? step.kind : null });
        cur = step ? step.from : null;
    }
    return { path, cost: dist.get(toKey) };
}

// ── polyline tidying ────────────────────────────────────────────────────────

const perpDistance = (p, a, b) => {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-9) return Math.hypot(p.x - a.x, p.z - a.z);
    return Math.abs((dz * p.x) - (dx * p.z) + (b.x * a.z) - (b.z * a.x)) / len;
};

/**
 * Ramer–Douglas–Peucker over a route polyline.
 *
 * A grid-derived path is a staircase of one-cell steps. Narrated verbatim it
 * produces forty "turn left / turn right" instructions for a straight walk down
 * a mall, and drawn verbatim it renders as a zigzag ribbon. Straightening it
 * first is what makes an imported route read like the sample estate's.
 */
export function simplifyPolyline(points, tolerance) {
    if (points.length < 3) return points.slice();
    const keep = new Uint8Array(points.length);
    keep[0] = 1;
    keep[points.length - 1] = 1;
    const stack = [[0, points.length - 1]];

    while (stack.length) {
        const [first, last] = stack.pop();
        let maxD = 0;
        let index = -1;
        for (let i = first + 1; i < last; i++) {
            const d = perpDistance(points[i], points[first], points[last]);
            if (d > maxD) { maxD = d; index = i; }
        }
        if (maxD > tolerance && index > 0) {
            keep[index] = 1;
            stack.push([first, index], [index, last]);
        }
    }

    const out = [];
    for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i]);
    return out;
}

// ── narration ───────────────────────────────────────────────────────────────

const bearing = (a, b) => {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    if (Math.abs(dx) >= Math.abs(dz)) return dx > 0 ? 'east' : 'west';
    return dz > 0 ? 'south' : 'north';
};

const TURN = {
    'north|east': 'right', 'north|west': 'left',
    'south|east': 'left', 'south|west': 'right',
    'east|south': 'right', 'east|north': 'left',
    'west|south': 'left', 'west|north': 'right',
};

/**
 * A climb through several levels on one core arrives as one instruction per
 * level. Nobody reads "take the escalator up" three times — collapse a run on
 * the same core and direction into a single step naming the final floor.
 */
function mergeVerticalRuns(steps) {
    const out = [];
    steps.forEach((step) => {
        const prev = out[out.length - 1];
        if (
            step.kind === 'vertical' && prev && prev.kind === 'vertical'
            && prev.core === step.core && prev.going === step.going
        ) {
            prev.levels += 1;
            prev.toFloorName = step.toFloorName;
            prev.text = `Take the ${prev.mode} ${prev.going} to ${prev.toFloorName}`;
            prev.detail = `${prev.core} · ${prev.levels} levels`;
            return;
        }
        out.push({ ...step });
    });
    return out;
}

function narrate(legs, from, to, landmarkAt) {
    const steps = [{ kind: 'start', text: `Start at ${from.label}`, detail: from.sub }];
    let lastLandmark = null;

    legs.forEach((leg) => {
        if (leg.transition) {
            const t = leg.transition;
            steps.push({
                kind: 'vertical',
                mode: t.mode,
                core: t.core,
                going: t.going,
                toFloorName: t.toFloorName,
                levels: 1,
                text: `Take the ${t.mode} ${t.going} to ${t.toFloorName}`,
                detail: t.core,
            });
        }

        const pts = leg.points;
        let runStart = pts[0];
        let runDir = null;
        let runDist = 0;

        const flushRun = () => {
            if (runDist < 4) { runDist = 0; return; }
            let landmark = landmarkAt ? landmarkAt(runStart, leg.floor) : null;
            // Three consecutive steps that all say "past Zara" tell the reader
            // nothing. A landmark is only worth naming once per approach.
            if (landmark && landmark === lastLandmark) landmark = null;
            if (landmark) lastLandmark = landmark;
            steps.push({
                kind: 'walk',
                text: `Head ${runDir} for ${Math.round(runDist)} m`,
                detail: landmark ? `past ${landmark}` : (leg.floorName ? `on ${leg.floorName}` : 'along the mall'),
            });
            runDist = 0;
        };

        for (let i = 1; i < pts.length; i++) {
            const dir = bearing(pts[i - 1], pts[i]);
            const seg = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].z - pts[i - 1].z);
            if (runDir && dir !== runDir) {
                flushRun();
                const turn = TURN[`${runDir}|${dir}`];
                if (turn) steps.push({ kind: 'turn', text: `Turn ${turn}`, detail: `now heading ${dir}` });
                runStart = pts[i - 1];
            }
            runDir = dir;
            runDist += seg;
        }
        flushRun();
    });

    steps.push({ kind: 'arrive', text: `Arrive at ${to.label}`, detail: to.sub });
    return mergeVerticalRuns(steps);
}

// ── public factory ──────────────────────────────────────────────────────────

/**
 * Bind a graph plus a set of endpoints into a router.
 *
 * @param {object}   cfg.graph        from makeGraph(), already populated
 * @param {Array}    cfg.endpoints    [{ id, label, sub, floor, nodeKey, kind }]
 * @param {Array}    cfg.floors       [{ id, name, level }]
 * @param {Function} [cfg.landmarkAt] (point, floorId) -> string|null, for colour
 * @param {number}   [cfg.simplifyTolerance] metres; 0 disables straightening
 */
export function createRouter({ graph, endpoints, floors, landmarkAt = null, simplifyTolerance = 1.6 }) {
    const byId = new Map(endpoints.map((e) => [e.id, e]));
    const floorById = new Map(floors.map((f) => [f.id, f]));
    const getEndpoint = (id) => byId.get(id) || null;

    function findRoute(fromId, toId) {
        const from = getEndpoint(fromId);
        const to = getEndpoint(toId);
        if (!from || !to) return null;
        if (from.nodeKey === to.nodeKey) return null;

        const result = shortestPath(graph, from.nodeKey, to.nodeKey);
        if (!result) return null;
        const { path } = result;

        // Walked distance only — the vertical hop is time, not metres.
        let distance = 0;
        let floorChanges = 0;
        for (let i = 1; i < path.length; i++) {
            if (path[i].viaKind === 'vertical') { floorChanges += 1; continue; }
            distance += Math.hypot(path[i].node.x - path[i - 1].node.x, path[i].node.z - path[i - 1].node.z);
        }
        const seconds = (distance / WALK_SPEED) + (floorChanges * FLOOR_CHANGE_SECONDS);

        // Split the polyline wherever the floor changes, so each floor draws its
        // own leg, and record what carried us between them.
        const legs = [];
        let current = null;
        path.forEach((p, i) => {
            const via = i > 0 ? path[i].viaKind : null;
            if (!current || via === 'vertical') {
                const prevNode = i > 0 ? path[i - 1].node : null;
                const floor = floorById.get(p.node.floor);
                let transition = null;
                if (via === 'vertical' && prevNode) {
                    const fromFloor = floorById.get(prevNode.floor);
                    const type = prevNode.amenityType || 'Escalator';
                    transition = {
                        mode: type === 'Lift' ? 'lift' : (type === 'Stairs' ? 'stairs' : 'escalator'),
                        core: prevNode.label || 'Vertical circulation',
                        going: (floor && fromFloor && floor.level > fromFloor.level) ? 'up' : 'down',
                        toFloorName: floor ? floor.name : p.node.floor,
                    };
                }
                current = {
                    floor: p.node.floor,
                    floorName: floor ? floor.name : p.node.floor,
                    points: [],
                    transition,
                    enteredVertically: !!transition,
                };
                legs.push(current);
            }
            current.points.push({ x: p.node.x, z: p.node.z });
        });

        const tidy = legs.map((leg) => ({
            ...leg,
            points: simplifyTolerance > 0 ? simplifyPolyline(leg.points, simplifyTolerance) : leg.points,
        }));

        return {
            from,
            to,
            distance: Math.round(distance),
            seconds: Math.round(seconds),
            minutes: Math.max(1, Math.round(seconds / 60)),
            floorChanges,
            segments: tidy,
            steps: narrate(tidy, from, to, landmarkAt),
        };
    }

    return { endpoints, getEndpoint, findRoute };
}
